import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Info, Search, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { formatCreateCampaignUpstreamError } from '@/utils/campaignCreate';
import { cn } from '@/utils/cn';

export type SbBrandLogo = {
  assetId: string;
  url: string;
  name: string;
};

const LOGO_MAX_BYTES = 1_048_576;
const LOGO_MIN_PX = 400;
const LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);

type SortOrder = 'newest' | 'oldest' | 'name';

const SORT_LABELS: Record<SortOrder, string> = {
  newest: 'Added: Newest to oldest',
  oldest: 'Added: Oldest to newest',
  name: 'Name',
};

function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const size = { width: image.naturalWidth, height: image.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    image.src = url;
  });
}

function dataUrlBytes(url: string): number | null {
  const match = /^data:[^;]+;base64,(.+)$/.exec(url);
  if (!match) return null;
  const body = match[1];
  const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function LogoTile({
  logo,
  loading,
}: {
  logo: SbBrandLogo | null;
  loading?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [logo?.url]);
  return (
    <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-md border border-border bg-page p-2">
      {logo?.url && !broken ? (
        <img
          src={logo.url}
          alt={logo.name || 'Brand logo'}
          referrerPolicy="no-referrer"
          className="h-full w-full object-contain"
          onError={() => setBroken(true)}
        />
      ) : loading ? (
        <span className="text-center text-xs text-foreground-muted">Loading logo…</span>
      ) : null}
    </div>
  );
}

function AssetCard({
  asset,
  selected,
  onSelect,
}: {
  asset: SbBrandLogo;
  selected: boolean;
  onSelect: () => void;
}) {
  const [dims, setDims] = useState<{ width: number; height: number } | null>(null);
  const bytes = dataUrlBytes(asset.url);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'overflow-hidden rounded-lg border bg-surface text-left',
        selected ? 'border-brand ring-2 ring-brand' : 'border-border hover:border-foreground-subtle'
      )}
    >
      <div className="flex h-24 items-center justify-center bg-page p-2">
        {asset.url ? (
          <img
            src={asset.url}
            alt=""
            referrerPolicy="no-referrer"
            className="max-h-full max-w-full object-contain"
            onLoad={(event) => {
              const image = event.currentTarget;
              if (image.naturalWidth > 0) {
                setDims({ width: image.naturalWidth, height: image.naturalHeight });
              }
            }}
          />
        ) : (
          <span className="text-xs text-foreground-subtle">No preview</span>
        )}
      </div>
      <div className="border-t border-border px-3 py-2">
        <p className="truncate text-sm text-foreground">{asset.name || 'Brand logo'}</p>
        <p className="mt-0.5 text-xs text-foreground-muted">
          {[bytes != null ? formatBytes(bytes) : null, dims ? `${dims.width} x ${dims.height} px` : null]
            .filter(Boolean)
            .join(' ')}
        </p>
      </div>
    </button>
  );
}

function preferredLogo(logos: SbBrandLogo[], brandName: string): SbBrandLogo | undefined {
  const withImage = logos.filter((logo) => logo.url);
  const needle = brandName.trim().toLowerCase();
  if (needle) {
    const matched = withImage.find((logo) => logo.name.toLowerCase().includes(needle));
    if (matched) return matched;
  }
  return withImage[0];
}

export function SbBrandLogoField({
  profileId,
  brandEntityId,
  brandName,
  brandsReady,
  disabled,
  value,
  onChange,
}: {
  profileId: string;
  brandEntityId: string;
  brandName: string;
  brandsReady: boolean;
  disabled: boolean;
  value: SbBrandLogo | null;
  onChange: (logo: SbBrandLogo | null) => void;
}) {
  const qc = useQueryClient();
  const menuId = useId();
  const pickerTitleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [logoTypeOn, setLogoTypeOn] = useState(true);
  const [sort, setSort] = useState<SortOrder>('newest');
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [aplusOn, setAplusOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoSelected = useRef(false);

  const logosQuery = useQuery({
    queryKey: ['sb-store-logos', profileId, brandEntityId],
    enabled: Boolean(profileId) && brandsReady,
    queryFn: async () => {
      const res = await adsApi.listSbStoreLogos(profileId, brandEntityId || undefined);
      if (!res.success) throw new Error(res.message || 'Failed to load logos');
      return res.data?.assets ?? [];
    },
    staleTime: 60_000,
    retry: false,
  });

  const logos = logosQuery.data ?? [];

  useEffect(() => {
    autoSelected.current = false;
  }, [brandEntityId]);

  useEffect(() => {
    const withImage = logos.filter((logo) => logo.url);
    if (withImage.length === 0) return;
    const next = preferredLogo(logos, brandName);
    if (!next) return;
    const match = value ? withImage.find((logo) => logo.assetId === value.assetId) : undefined;
    if (!value) {
      if (autoSelected.current) return;
      autoSelected.current = true;
      onChange(next);
      return;
    }
    if (!match) {
      onChange(next);
      return;
    }
    if (match.url !== value.url) onChange(match);
  }, [brandName, logos, onChange, value]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      setPickerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [pickerOpen]);

  const visibleLogos = useMemo(() => {
    const filtered = logos.filter((logo) => {
      const needle = query.trim().toLowerCase();
      if (!needle) return true;
      return (logo.name || 'Brand logo').toLowerCase().includes(needle);
    });
    const ordered = [...filtered];
    if (sort === 'newest') ordered.reverse();
    if (sort === 'name') ordered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return ordered;
  }, [logos, query, sort]);

  const openPicker = () => {
    setDraftId(value?.assetId ?? null);
    setQuery('');
    setLogoTypeOn(true);
    setSort('newest');
    setMenuOpen(false);
    setPickerOpen(true);
  };

  const applyPicker = () => {
    const picked = logos.find((logo) => logo.assetId === draftId);
    if (!picked) return;
    autoSelected.current = true;
    onChange(picked);
    setError(null);
    setPickerOpen(false);
  };

  const upload = async (file: File) => {
    setError(null);
    const contentType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    if (!LOGO_TYPES.has(contentType)) {
      setError('Logo must be a PNG, JPEG, or GIF.');
      return;
    }
    if (file.size >= LOGO_MAX_BYTES) {
      setError('Logo must be smaller than 1 MB.');
      return;
    }
    let size: { width: number; height: number };
    try {
      size = await readImageSize(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that image.');
      return;
    }
    if (size.width < LOGO_MIN_PX || size.height < LOGO_MIN_PX) {
      setError(`Logo must be at least ${LOGO_MIN_PX} by ${LOGO_MIN_PX} pixels.`);
      return;
    }
    setBusy(true);
    try {
      const res = await adsApi.uploadSbStoreLogo(profileId, file, brandEntityId || undefined);
      if (!res.success || !res.data?.assetId) {
        setError(res.message || 'Logo upload failed.');
        return;
      }
      autoSelected.current = true;
      onChange({
        assetId: res.data.assetId,
        url: res.data.url || URL.createObjectURL(file),
        name: res.data.name || file.name,
      });
      setMenuOpen(false);
      await qc.invalidateQueries({ queryKey: ['sb-store-logos', profileId, brandEntityId] });
    } catch (err) {
      setError(formatCreateCampaignUpstreamError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5" ref={rootRef}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <span className="text-sm font-semibold text-foreground">Logo</span>
            <span
              title="Shown beside the brand name. PNG, JPEG, or GIF, at least 400 by 400 pixels, under 1 MB."
              className="inline-flex text-foreground-subtle"
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">
                Shown beside the brand name. PNG, JPEG, or GIF, at least 400 by 400 pixels, under 1 MB.
              </span>
            </span>
            <span className="text-sm text-foreground-muted">(optional)</span>
          </div>
          <LogoTile logo={value} loading={logosQuery.isPending && !value} />
          {logosQuery.isError && !value ? (
            <p className="mt-2 text-xs text-error">
              Couldn&apos;t load logos from this account.{' '}
              <button type="button" className="text-brand hover:underline" onClick={() => void logosQuery.refetch()}>
                Try again
              </button>
            </p>
          ) : null}
        </div>
        <div className="relative">
          <button
            type="button"
            disabled={disabled || busy}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((current) => !current)}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-surface px-3 text-sm text-foreground hover:bg-surface-muted disabled:opacity-50"
          >
            Change
            <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          </button>
          {menuOpen ? (
            <div
              id={menuId}
              className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-border bg-surface py-1 shadow-lg"
            >
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm text-foreground hover:bg-surface-muted"
                onClick={openPicker}
              >
                Choose from creative assets
              </button>
              <button
                type="button"
                disabled={disabled || busy}
                className="block w-full px-4 py-2 text-left text-sm text-foreground hover:bg-surface-muted disabled:opacity-50"
                onClick={() => fileRef.current?.click()}
              >
                {busy ? 'Uploading…' : 'Upload logo'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void upload(file);
        }}
      />
      {error ? <p className="mt-2 text-xs text-error">{error}</p> : null}

      {pickerOpen ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={pickerTitleId}
            className="mt-8 flex max-h-[min(78vh,36rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 id={pickerTitleId} className="text-base font-semibold text-foreground">
                Choose from creative assets
              </h2>
              <button type="button" aria-label="Close" onClick={() => setPickerOpen(false)}>
                <X className="h-5 w-5 text-foreground-muted" />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-foreground-muted">Filter</span>
                {logoTypeOn ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-1 text-xs text-surface"
                    onClick={() => setLogoTypeOn(false)}
                  >
                    Type: Logo
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                ) : null}
                <button type="button" className="text-sm text-brand" onClick={() => setLogoTypeOn(false)}>
                  Remove all
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="relative min-w-[14rem] flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-subtle" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by name, tag or ASIN"
                    className="h-9 w-full rounded-lg border border-border pl-8 pr-3 text-sm"
                    aria-label="Search creative assets"
                  />
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex h-9 items-center gap-1 rounded-full border border-border px-3 text-sm"
                    aria-expanded={filterOpen}
                    onClick={() => setFilterOpen((open) => !open)}
                  >
                    Filter by
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  {filterOpen ? (
                    <ul className="absolute left-0 z-10 mt-1 w-40 rounded-lg border border-border bg-surface py-1 shadow-lg">
                      <li>
                        <button
                          type="button"
                          className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-muted"
                          onClick={() => {
                            setLogoTypeOn(true);
                            setFilterOpen(false);
                          }}
                        >
                          Type: Logo
                        </button>
                      </li>
                    </ul>
                  ) : null}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex h-9 items-center gap-1 rounded-full border border-border px-3 text-sm"
                    aria-expanded={sortOpen}
                    onClick={() => setSortOpen((open) => !open)}
                  >
                    Sort by: {SORT_LABELS[sort]}
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  {sortOpen ? (
                    <ul className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-border bg-surface py-1 shadow-lg">
                      {(Object.keys(SORT_LABELS) as SortOrder[]).map((id) => (
                        <li key={id}>
                          <button
                            type="button"
                            className={cn(
                              'block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-muted',
                              sort === id && 'font-medium'
                            )}
                            onClick={() => {
                              setSort(id);
                              setSortOpen(false);
                            }}
                          >
                            {SORT_LABELS[id]}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <p className="font-medium text-foreground">
                  {visibleLogos.length} asset{visibleLogos.length === 1 ? '' : 's'}
                </p>
                <label className="inline-flex items-center gap-2 text-sm text-foreground">
                  A+ content
                  <button
                    type="button"
                    role="switch"
                    aria-checked={aplusOn}
                    aria-label="A+ content"
                    onClick={() => setAplusOn((on) => !on)}
                    className={cn(
                      'relative h-5 w-9 rounded-full transition-colors',
                      aplusOn ? 'bg-brand' : 'bg-foreground-subtle'
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-4 w-4 rounded-full bg-surface transition-transform',
                        aplusOn ? 'left-4' : 'left-0.5'
                      )}
                    />
                  </button>
                </label>
              </div>
              {logosQuery.isLoading ? <p className="text-sm text-foreground-muted">Loading logos…</p> : null}
              {logosQuery.isError ? (
                <p className="text-sm text-error">Couldn&apos;t load logos from this account.</p>
              ) : null}
              {!logosQuery.isLoading && visibleLogos.length === 0 ? (
                <p className="text-sm text-foreground-muted">No brand logos match this search.</p>
              ) : null}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {visibleLogos.map((logo) => (
                  <AssetCard
                    key={logo.assetId}
                    asset={logo}
                    selected={draftId === logo.assetId}
                    onSelect={() => setDraftId(logo.assetId)}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
              <button type="button" className="text-sm text-brand" onClick={() => setPickerOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!draftId}
                onClick={applyPicker}
                className="rounded-full border border-border px-4 py-1.5 text-sm disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
